import type {
  UserQuestDetail,
  UserQuestProgress,
} from "Infrastructure/Api/Api";
import { RequestDetailCard } from "Infrastructure/Components/DetailCards/RequestDetailCard";
import { useFilter } from "Infrastructure/Contexts/FilterContext";
import { useUserQuestStatusFilterOptions } from "Infrastructure/Hooks/useUserQuestStatusFilterOptions";
import type { ResourceDictionary } from "Infrastructure/Translations/ResourceDictionary";
import { useResource } from "Infrastructure/Translations/Resources";
import { useUserQuestQuery } from "Requests/Queries/useUserQuestQuery";
import { Typography } from "@mui/material";
import { useMemo, useState } from "react";
import type { ObjPathProxy } from "ts-object-path";
import { CardContainer } from "./CardContainer";
import { RequestList } from "./RequestList";
import { TitleWithFilter } from "./Shared/TitleWithFilter";

interface RequestContainerProps {
  progressFilter?: UserQuestProgress | "all";
  titleKey: ObjPathProxy<ResourceDictionary, string>;
  emptyStateKey: ObjPathProxy<ResourceDictionary, string>;
  loadingKey: ObjPathProxy<ResourceDictionary, string>;
  errorKey: ObjPathProxy<ResourceDictionary, string>;
  showFilter?: boolean;
}

export const RequestContainer = ({
  progressFilter,
  titleKey,
  emptyStateKey,
  loadingKey,
  errorKey,
  showFilter = false,
}: RequestContainerProps) => {
  const { t } = useResource();
  const { data: userQuests, isLoading, error } = useUserQuestQuery();
  const [selectedRequest, setSelectedRequest] =
    useState<UserQuestDetail | null>(null);

  const [filter, setFilter] = useFilter(
    "requestsFilter",
    progressFilter || "all",
  );

  const activeFilter = showFilter ? filter : progressFilter || "all";

  const filteredRequests = useMemo(() => {
    if (!userQuests?.items) {
      return [];
    }
    if (activeFilter === "all") {
      return userQuests.items;
    }
    return userQuests.items.filter((q) => q.progress === activeFilter);
  }, [userQuests?.items, activeFilter]);

  const filterOptions = useUserQuestStatusFilterOptions(
    userQuests?.items || [],
  );

  if (isLoading) {
    return (
      <CardContainer title={t(titleKey)}>
        <Typography color="text.secondary">{t(loadingKey)}</Typography>
      </CardContainer>
    );
  }

  if (error) {
    return (
      <CardContainer title={t(titleKey)}>
        <Typography color="error">{t(errorKey)}</Typography>
      </CardContainer>
    );
  }

  return (
    <>
      <CardContainer
        title={
          showFilter ? (
            <TitleWithFilter
              title={t(titleKey)}
              options={filterOptions}
              value={filter}
              onChange={setFilter}
            />
          ) : (
            t(titleKey)
          )
        }
        enableScroll
        maxHeight="calc(100vh - 170px)"
      >
        <RequestList
          requests={filteredRequests}
          emptyMessage={t(emptyStateKey)}
          onRequestClick={setSelectedRequest}
        />
      </CardContainer>

      <RequestDetailCard
        request={selectedRequest}
        open={!!selectedRequest}
        onClose={() => setSelectedRequest(null)}
      />
    </>
  );
};
